import { IClient } from '~/types/client';

// Clients running the AIRPORT experience. Despite the name of the free-play
// helpers below, adding an ID here switches on FOUR behaviours at once — check
// all of them before adding a client for just one of them:
//   1. free-play ELIGIBILITY — actually on only when the Firestore flag
//      `freePlayEnabled` is also true (see isFreePlayClient);
//   2. fullscreen popup styling (GlobalPopup);
//   3. the big auto-QR shown while nobody is playing (QRCodeButton) — free-play off only;
//   4. the per-passenger language picker on the gamepad, always on (utils/playerLanguage).
//
// `demo-QAIA` — espace de DÉMO temporaire pour le prospect Queen Alia Int'l
// Airport (Amman, actif du groupe ADP), pour qu'ils testent le vrai free-play
// plutôt que la rotation auto. Les 4 comportements ci-dessus sont voulus ici :
// on veut leur montrer l'expérience aéroport complète, sélecteur de langue inclus.
// TEMPORAIRE : retirer cette entrée quand la démo n'a plus lieu d'être
// (l'espace Firestore est supprimé/désactivé en parallèle).
export const AIRPORT_CLIENT_IDS = ['airport-ORY', 'airport-BOD', 'airport-MRS', 'demo-QAIA'] as const;

export type AirportClientID = (typeof AIRPORT_CLIENT_IDS)[number];

export function isAirportClient(clientID?: string): clientID is AirportClientID {
    return !!clientID && (AIRPORT_CLIENT_IDS as readonly string[]).includes(clientID);
}

/**
 * Free-play is the airport mode where players scan, choose their game, and can
 * vote to switch — replacing the time-based auto rotation. Activated per-client
 * via the `freePlayEnabled` Firestore flag, restricted to airport clients to
 * avoid unintended activation in bars/SaaS.
 *
 * Both gates are required: the client must be in AIRPORT_CLIENT_IDS AND have
 * `freePlayEnabled: true`. Rollback to auto-rotation is therefore trivial and
 * instant — set the Firestore flag back to false (no deploy needed; the screen
 * reads it via a real-time subscription).
 */
export function isFreePlayClient(client?: IClient | null): boolean {
    if (!client) return false;
    return isAirportClient(client.id) && client.freePlayEnabled;
}

/**
 * Per-client game exclusions — retire un jeu du catalogue free-play ET de la
 * rotation auto pour UN client donné, sans toucher aux autres. Gardé en dur ici
 * (et pas en Firestore) pour que la raison soit relue en PR et le retrait trivial.
 *
 * `airport-ORY` — Footboared retiré à la demande d'ADP/Orly pendant la Coupe du
 * Monde (crainte d'un litige de marque avec la FIFA). TEMPORAIRE : supprimer cette
 * entrée à la fin de la compétition pour réactiver le jeu à Orly.
 */
export const EXCLUDED_GAME_IDS_BY_CLIENT: Record<string, string[]> = {
    'airport-ORY': ['footboared'],
    // `demo-QAIA` — UnPop (jeu de tir) retiré du catalogue proposé au prospect
    // aéroport d'Amman : le jeu attend encore l'aval de nos contacts aéroport,
    // on ne l'expose pas en démo commerciale. TEMPORAIRE, comme l'espace démo.
    'demo-QAIA': ['unpop'],
};

/** Retire de `gameIDs` les jeux exclus pour ce client (renvoie la liste inchangée
    si le client n'a aucune exclusion). Utilisé partout où une liste de jeux est
    proposée à un écran / gamepad. */
export function excludeGamesForClient(gameIDs: string[], clientID?: string | null): string[] {
    const excluded = (clientID && EXCLUDED_GAME_IDS_BY_CLIENT[clientID]) || [];
    return excluded.length ? gameIDs.filter((id) => !excluded.includes(id)) : gameIDs;
}
